/**
 * Autosave status state machine for the note editor.
 *
 * Drives the colored status dot:
 *  - saved        🟢 green  — persisted, no pending edits
 *  - saving       🔵 blue   — a debounced save is in flight (online)
 *  - draftOnline  🟡 yellow — online, dirty edits held between keystroke & save
 *  - offlineClean ⚪ grey   — offline, no unsaved edits on this note
 *  - offlineDraft 🟡 yellow — offline, unsaved edits held as a localStorage draft
 *  - error        🔴 red    — last online save failed (will retry on next change)
 */
export type NoteAutosaveStatus =
  | 'saved'
  | 'saving'
  | 'draftOnline'
  | 'offlineClean'
  | 'offlineDraft'
  | 'error';

export interface AutosaveInputs {
  /** Network connectivity. */
  isOnline: boolean;
  /** There are edits that differ from what's persisted on the server. */
  dirty: boolean;
  /** A save request is currently in flight. */
  saving: boolean;
  /** The last online save attempt errored. */
  error: boolean;
  /** A localStorage draft exists for this note (offline edits held). */
  hasDraft: boolean;
}

/**
 * Pure mapping from autosave inputs to a single status.
 *
 * Precedence:
 *  1. Offline dominates: offline + (dirty|draft) → offlineDraft; else offlineClean.
 *  2. Online: saving → saving; error → error; dirty → draftOnline; else saved.
 */
export function resolveNoteAutosaveStatus(
  inputs: AutosaveInputs,
): NoteAutosaveStatus {
  const { isOnline, dirty, saving, error, hasDraft } = inputs;

  if (!isOnline) {
    return dirty || hasDraft ? 'offlineDraft' : 'offlineClean';
  }

  if (saving) return 'saving';
  if (error) return 'error';
  if (dirty) return 'draftOnline';
  return 'saved';
}

export interface StatusPresentation {
  /** Theme color token family for the dot. */
  color: 'success' | 'info' | 'warning' | 'error' | 'disabled';
  label: string;
}

/** Presentation (dot color + label) for each status. Pure lookup. */
export function noteAutosaveStatusPresentation(
  status: NoteAutosaveStatus,
): StatusPresentation {
  switch (status) {
    case 'saved':
      return { color: 'success', label: 'Saved' };
    case 'saving':
      return { color: 'info', label: 'Saving…' };
    case 'draftOnline':
      return { color: 'warning', label: 'Unsaved changes' };
    case 'offlineClean':
      return { color: 'disabled', label: 'Offline' };
    case 'offlineDraft':
      return { color: 'warning', label: 'Offline — draft saved' };
    case 'error':
      return { color: 'error', label: 'Save failed — retrying' };
  }
}
